import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, TrendingUp, TrendingDown, Activity, BarChart2, BarChart3, Volume2, Sparkles, ArrowLeft, Layers, ListFilter, Info, X, ExternalLink, Zap, SlidersHorizontal, Share2, Loader, BookOpen } from 'lucide-react';
import ScannerShareCard from '../components/ScannerShareCard';
import { captureAndShare } from '../utils/shareImage';
import toast from 'react-hot-toast';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import TradingChart from '../components/charts/TradingChart';
import OrderModal from '../components/OrderModal';
import { resolveTradeOptions, buildStructureLevels, computeOptionMetrics } from '../utils/resolveTradeOptions';
import { formatPrice, formatDate } from '../utils/formatters';
import { BULLISH_PATTERNS, computePatternTrade, CATEGORY_ICONS, PATTERN_INFO, STATUS_CONFIG, SCANNER_INFO, SCANNER_DETAIL, SCANNER_CATEGORY_INFO, SCANNER_TO_INDICATOR, SCANNER_INVERT_RANK, getDynamicDescription, SIGNAL_STATUS, SCANNER_CATEGORY_MAP, SCANNER_CATEGORY_LABELS, getSignalStatus, formatSignalAge, computeScannerSLFrontend } from './scanners/scannerConstants';


const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function ScannersPage() {
  const { colors } = useTheme();
  const { prices: socketPrices } = useSocket();
  const appMode = 'learn';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState('patterns'); // 'patterns' | 'scanners' | 'filter'
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedScanner, setSelectedScanner] = useState(null);
  const [scannerResults, setScannerResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  // Pattern state
  const [patterns, setPatterns] = useState([]);
  const [groupedPatterns, setGroupedPatterns] = useState({});
  const [patternsLoading, setPatternsLoading] = useState(false);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [patternMode, setPatternMode] = useState('swing');       // 'swing' | 'intraday'
  const [intradayTf, setIntradayTf] = useState('5min');          // '5min' | '15min'
  // Chart modal: which pattern card was clicked
  const [chartPattern, setChartPattern] = useState(null);
  // Info popover: which pattern type header info icon is open
  const [infoOpen, setInfoOpen] = useState(null);
  const infoRef = useRef(null);
  // Scanner info popovers
  const [scannerCatInfoOpen, setScannerCatInfoOpen] = useState(null);
  const [scannerSignalInfoOpen, setScannerSignalInfoOpen] = useState(null);
  const scannerInfoRef = useRef(null);
  // Screener dropdown refs
  const screenerTechRef    = useRef(null);
  const screenerPatternRef = useRef(null);

  // Pattern — OrderModal
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderModalSide, setOrderModalSide] = useState('BUY');
  const [orderModalStock, setOrderModalStock] = useState(null);   // { symbol, company_name, current_price }
  const [orderModalPrefill, setOrderModalPrefill] = useState(null);
  const [riskPct, setRiskPct] = useState(1.0);
  const [capital, setCapital] = useState(1000000);

  // Stock Filter state (legacy — unused)
  const [filterScanner, setFilterScanner] = useState(null);
  const [filterResults, setFilterResults] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);

  // Stock Screener state
  const [screenerTimeframe, setScreenerTimeframe]         = useState('swing');
  const [screenerSearch, setScreenerSearch]               = useState('');
  const [screenerSector, setScreenerSector]               = useState(null);
  const [screenerPriceRange, setScreenerPriceRange]       = useState(null);
  const [screenerSortBy, setScreenerSortBy]               = useState('name');
  const [screenerSortDir, setScreenerSortDir]             = useState('asc');
  const [screenerTechFilters, setScreenerTechFilters]     = useState(new Set());
  const [screenerPatternFilters, setScreenerPatternFilters] = useState(new Set());
  const [screenerStocks, setScreenerStocks]               = useState([]);
  const [screenerScanResults, setScreenerScanResults]     = useState([]);
  const [screenerPatterns, setScreenerPatterns]           = useState([]);
  const [screenerLoading, setScreenerLoading]             = useState(false);
  const [screenerTechOpen, setScreenerTechOpen]           = useState(false);
  const [screenerPatternOpen, setScreenerPatternOpen]     = useState(false);

  // Scanner signals state (Technical tab)
  const [scannerSignals, setScannerSignals] = useState([]);
  const [scannerSignalsLoading, setScannerSignalsLoading] = useState(false);
  const [scannerSignalsGrouped, setScannerSignalsGrouped] = useState({});
  const [chartSignal, setChartSignal] = useState(null);
  const [scannerSocialOpen, setScannerSocialOpen] = useState(false);
  const [scannerCapturing, setScannerCapturing] = useState(false);
  const scannerCardRef = useRef(null);
  const [tradeOptionsOpen, setTradeOptionsOpen] = useState(false);
  const [tradeResolution, setTradeResolution] = useState(null);
  const [tradeOptionsSignal, setTradeOptionsSignal] = useState(null);
  const [tradeOptionsSide, setTradeOptionsSide] = useState('BUY');
  // Timeframe + filter state
  const [scannerTimeframe, setScannerTimeframe] = useState('swing');
  const [scannerIntradayTf, setScannerIntradayTf] = useState('5min');
  const [activeScannerCategory, setActiveScannerCategory] = useState('ALL');
  // Chart patterns filter + collapse
  const [activePatternFilter, setActivePatternFilter] = useState('ALL');
  const [collapsedPatterns, setCollapsedPatterns] = useState(new Set());
  const [collapsedScanners, setCollapsedScanners] = useState(new Set());
  const [collapsedGuideCategories, setCollapsedGuideCategories] = useState(
    () => new Set(Object.keys(SCANNER_CATEGORY_LABELS))
  );
  const [collapsedGuideScanners, setCollapsedGuideScanners] = useState(
    () => new Set(Object.keys(SCANNER_INFO))
  );
  // Filter bottom sheet
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Confluence: multi-scanner selection
  const [confluenceMode, setConfluenceMode] = useState(false);
  const [selectedScanners, setSelectedScanners] = useState(new Set());

  // Live prices for pattern/filter cards: { 'RELIANCE.NS': { price, prev_close, change_pct } }
  const [scanLastDate, setScanLastDate] = useState(null);


  useEffect(() => {
    fetchCategories();
    const tabParam      = searchParams.get('tab');
    const scannerParam  = searchParams.get('scanner');
    const categoryParam = searchParams.get('category');
    if (tabParam)      setActiveTab(tabParam);
    if (scannerParam)  { setSelectedScanner(scannerParam); fetchScannerResults(scannerParam); }
    if (categoryParam) { setSelectedCategory(categoryParam); }
  }, [appMode]);

  useEffect(() => {
    if (activeTab === 'patterns') fetchPatterns(patternMode, intradayTf);
  }, [activeTab, patternMode, intradayTf]);


  const fetchScreenerData = async (tf) => {
    setScreenerLoading(true);
    try {
      const [stocksRes, scanRes, patternRes] = await Promise.all([
        screenerStocks.length ? null : axios.get(`${API_URL}/stocks`),
        axios.get(`${API_URL}/scanners/results`, { params: { limit: 500, timeframe: tf } }),
        tf === 'swing'
          ? axios.get(`${API_URL}/patterns`)
          : axios.get(`${API_URL}/patterns/intraday`, { params: { timeframe: tf } }),
      ]);
      if (stocksRes) setScreenerStocks(stocksRes.data || []);
      setScreenerScanResults(scanRes.data || []);
      setScreenerPatterns(patternRes.data?.patterns || []);
    } catch (_) {
      toast.error('Failed to load screener data');
    } finally {
      setScreenerLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'filter') fetchScreenerData(screenerTimeframe);
  }, [activeTab, screenerTimeframe]); // eslint-disable-line

  const fetchScannerSignals = async (tf, intradayTf) => {
    const resolvedTf = tf || scannerTimeframe;
    const timeframe = resolvedTf === 'intraday' ? (intradayTf || scannerIntradayTf) : 'swing';
    try {
      setScannerSignalsLoading(true);
      const res = await axios.get(`${API_URL}/scanners/results`, { params: { limit: 200, timeframe } });
      const raw = res.data || [];

      // Compute percentile rank per scanner group (higher % = stronger signal)
      const byScanner = {};
      raw.forEach(sig => {
        if (!byScanner[sig.scanner_name]) byScanner[sig.scanner_name] = [];
        byScanner[sig.scanner_name].push(sig.scan_value ?? 0);
      });
      const signals = raw.map(sig => {
        const group = byScanner[sig.scanner_name] || [];
        if (group.length <= 1) return { ...sig, strengthPct: 50 };
        const val    = sig.scan_value ?? 0;
        const sorted = [...group].sort((a, b) => a - b);
        const rank   = sorted.indexOf(val);
        const pct    = Math.round((rank / (sorted.length - 1)) * 100);
        // Inverted: lower value = stronger (e.g. RSI_OVERSOLD)
        const strengthPct = SCANNER_INVERT_RANK.has(sig.scanner_name) ? 100 - pct : pct;
        return { ...sig, strengthPct };
      });

      setScannerSignals(signals);
      const grouped = {};
      signals.forEach(sig => {
        const cat = SCANNER_CATEGORY_MAP[sig.scanner_name] || 'OTHER';
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(sig);
      });
      setScannerSignalsGrouped(grouped);
    } catch (_) {
      toast.error('Failed to load scanner signals');
    } finally {
      setScannerSignalsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'scanners') fetchScannerSignals(scannerTimeframe, scannerIntradayTf);
  }, [activeTab, scannerTimeframe, scannerIntradayTf]);


  const fetchCategories = async () => {
    try {
      const [catRes, dateRes] = await Promise.allSettled([
        axios.get(`${API_URL}/scanners/categories`),
        axios.get(`${API_URL}/scanners/last-date`),
      ]);
      if (catRes.status === 'fulfilled') setCategories(catRes.value.data);
      if (dateRes.status === 'fulfilled') setScanLastDate(dateRes.value.data?.as_of);
    } catch (error) {
      toast.error('Failed to load scanners');
    } finally {
      setLoading(false);
    }
  };

  const fetchScannerResults = async (scannerName, pageNum = 1) => {
    try {
      setResultsLoading(true);
      const response = await axios.get(`${API_URL}/scanners/${scannerName}/stocks`, { params: { page: pageNum, page_size: 20 } });
      setScannerResults(response.data.results);
      setTotalResults(response.data.total);
      setPage(pageNum);
    } catch (error) {
      toast.error('Failed to load results');
    } finally {
      setResultsLoading(false);
    }
  };

  const fetchPatterns = async (mode = 'swing', tf = '5min') => {
    try {
      setPatternsLoading(true);
      const url = mode === 'intraday'
        ? `${API_URL}/patterns/intraday?timeframe=${tf}`
        : `${API_URL}/patterns`;
      const response = await axios.get(url);
      setPatterns(response.data.patterns || []);
      setGroupedPatterns(response.data.grouped || {});
    } catch (error) {
      console.error('Failed to fetch patterns', error);
      toast.error('Failed to load chart patterns');
    } finally {
      setPatternsLoading(false);
    }
  };

  // Fetch capital (live virtual_balance) + risk profile once on mount
  useEffect(() => {
    const fetchRiskAndCapital = async () => {
      try {
        const [meRes, riskRes] = await Promise.allSettled([
          axios.get(`${API_URL}/auth/me`),
          axios.get(`${API_URL}/auth/profile`),
        ]);
        if (meRes.status === 'fulfilled') {
          const bal = meRes.value.data?.virtual_balance;
          if (bal) setCapital(parseFloat(bal));
        }
        if (riskRes.status === 'fulfilled') {
          const pct = riskRes.value.data?.recommended_risk_percent;
          if (pct) setRiskPct(parseFloat(pct));
        }
      } catch (_) {}
    };
    fetchRiskAndCapital();
  }, []);

  const openTradePanel = (pattern, side) => {
    const trade = computePatternTrade(pattern, side);
    if (!trade) { toast.error('Cannot compute trade levels for this pattern'); return; }
    // Deploy riskPct% of capital as position size (consistent with OrderModal risk check)
    const deployAmt = capital * (riskPct / 100);
    const qty = Math.max(1, Math.floor(deployAmt / trade.entry));
    const sym = pattern.symbol?.replace('.NS', '');
    const livePrice = socketPrices[pattern.symbol]?.price ?? trade.entry;

    setOrderModalSide(side);
    setOrderModalStock({ symbol: sym, company_name: sym, current_price: livePrice });
    const PATTERN_TAGS = {
      double_bottom:              ['breakout', 'support'],
      triple_bottom:              ['breakout', 'support'],
      head_and_shoulders:         ['breakout', 'resistance'],
      inverse_head_and_shoulders: ['breakout', 'support'],
      ascending_triangle:         ['breakout', 'trendline'],
      descending_triangle:        ['breakout', 'trendline'],
      bull_flag:                  ['breakout', 'trendline'],
      bear_flag:                  ['breakout', 'trendline'],
      support_bounce:             ['support'],
      resistance_rejection:       ['resistance'],
      double_top:                 ['breakout', 'resistance'],
    };
    setOrderModalPrefill({
      orderType:     side,
      productType:   'SWING',
      tradeReason:   'scanner',
      technicalTags: PATTERN_TAGS[pattern.pattern_type] || ['breakout'],
      notes:         `${pattern.pattern_type.replace(/_/g,' ')} pattern — ${trade.warnings.map(w => w.text).join(' | ')}`,
    });
    setOrderModalOpen(true);
  };

  const openScannerTradePanel = async (signal, side) => {
    const sym = signal.symbol?.replace('.NS', '');
    const entry = socketPrices[signal.symbol]?.price;
    if (!entry) { toast.error('No live price — wait for prices to load'); return; }

    let feat = null;
    let recentCandles = [];
    let patternForStock = null;
    try {
      const [techRes, chartRes, patternRes] = await Promise.allSettled([
        axios.get(`${API_URL}/stocks/${sym}/technicals`),
        axios.get(`${API_URL}/chart/${sym}`, { params: { interval: '1d' } }),
        axios.get(`${API_URL}/patterns`),
      ]);
      if (techRes.status === 'fulfilled') feat = techRes.value.data;
      if (chartRes.status === 'fulfilled') {
        recentCandles = (chartRes.value.data?.candles || chartRes.value.data || []).slice(-15);
      }
      if (patternRes.status === 'fulfilled') {
        const allPatterns = patternRes.value.data?.patterns || [];
        patternForStock = allPatterns.find(
          p => p.symbol === signal.symbol && p.pattern_status !== 'EXHAUSTED'
        ) || null;
      }
    } catch (_) {}

    let scannerSL = computeScannerSLFrontend(signal.scanner_name, feat, socketPrices, signal.symbol);
    const info = SCANNER_INFO[signal.scanner_name] || {};
    const SCANNER_TAGS = info.direction === 'bullish' ? ['breakout'] : info.direction === 'bearish' ? ['breakout'] : ['breakout'];

    // Direction guard: BUY needs SL < entry, SELL needs SL > entry
    if (scannerSL) {
      if (side === 'BUY'  && scannerSL >= entry) scannerSL = parseFloat((entry * 0.985).toFixed(2));
      if (side === 'SELL' && scannerSL <= entry) scannerSL = parseFloat((entry * 1.015).toFixed(2));
    }

    if (!scannerSL) {
      setOrderModalSide(side);
      setOrderModalStock({ symbol: sym, company_name: signal.company_name || sym, current_price: entry });
      setOrderModalPrefill({
        orderType: side, productType: 'SWING',
        tradeReason: 'scanner', technicalTags: SCANNER_TAGS,
        notes: `${info.label || signal.scanner_name} scanner signal`,
      });
      setOrderModalOpen(true);
      return;
    }

    const slDist = Math.abs(entry - scannerSL);
    const scannerTarget = side === 'BUY'
      ? parseFloat((entry + slDist * 2).toFixed(2))
      : parseFloat((entry - slDist * 2).toFixed(2));

    const optionB = {
      entry: parseFloat(entry.toFixed(2)), sl: parseFloat(scannerSL.toFixed(2)),
      target: scannerTarget, source: 'scanner',
      label: `${info.label || signal.scanner_name} SL`,
    };

    let optionA = null;
    if (patternForStock) {
      const patternTrade = computePatternTrade(patternForStock, side);
      if (patternTrade) {
        optionA = {
          entry: patternTrade.entry, sl: patternTrade.sl, target: patternTrade.target,
          source: 'pattern',
          label: `${PATTERN_INFO[patternForStock.pattern_type]?.label || patternForStock.pattern_type} SL`,
        };
      }
    }

    const structureLevels = buildStructureLevels(feat, patternForStock, recentCandles);
    const atr = feat?.atr_14;

    if (optionA) {
      const resolution = resolveTradeOptions(optionA, optionB, structureLevels, atr, capital, riskPct);
      setTradeResolution(resolution);
      setTradeOptionsSignal(signal);
      setTradeOptionsSide(side);
      setTradeOptionsOpen(true);
    } else {
      const metrics = computeOptionMetrics(optionB.entry, optionB.sl, optionB.target, capital, riskPct);
      setOrderModalSide(side);
      setOrderModalStock({ symbol: sym, company_name: signal.company_name || sym, current_price: entry });
      setOrderModalPrefill({
        orderType: side, productType: 'SWING',
        tradeReason: 'scanner', technicalTags: SCANNER_TAGS,
        notes: `${info.label || signal.scanner_name} scanner signal`,
      });
      setOrderModalOpen(true);
    }
  };

  const openTradeFromResolution = (option) => {
    if (!tradeOptionsSignal || !tradeResolution) return;
    const sym = tradeOptionsSignal.symbol?.replace('.NS', '');
    const entry = socketPrices[tradeOptionsSignal.symbol]?.price || option.entry;
    setTradeOptionsOpen(false);
    setOrderModalSide(tradeOptionsSide);
    setOrderModalStock({ symbol: sym, company_name: tradeOptionsSignal.company_name || sym, current_price: entry });
    setOrderModalPrefill({
      orderType: tradeOptionsSide, productType: 'SWING',
      tradeReason: option.source === 'pattern' ? 'scanner' : 'technical',
      technicalTags: ['breakout'],
      notes: `${option.label} — ${tradeResolution.reason}`,
    });
    setOrderModalOpen(true);
  };

  const formatDetectedAge = (detectedAt) => {
    if (!detectedAt) return null;
    const diff = Date.now() - new Date(detectedAt).getTime();
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (days >= 1) return `${days}d ago`;
    if (hours >= 1) return `${hours}h ago`;
    const mins = Math.floor(diff / 60000);
    return `${mins}m ago`;
  };

  const fetchFilterResults = async (scannerName) => {
    try {
      setFilterLoading(true);
      setFilterResults([]);
      const response = await axios.get(`${API_URL}/scanners/${scannerName}/stocks`, { params: { page: 1, page_size: 50 } });
      setFilterResults(response.data.results || []);
    } catch (error) {
      toast.error('Failed to load filter results');
    } finally {
      setFilterLoading(false);
    }
  };

  const handleFilterScannerSelect = (scannerName) => {
    if (filterScanner === scannerName) {
      setFilterScanner(null);
      setFilterResults([]);
    } else {
      setFilterScanner(scannerName);
      fetchFilterResults(scannerName);
    }
  };

  const handleCategorySelect = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setSelectedScanner(null);
    setScannerResults([]);
  };

  const handleScannerSelect = (scannerName) => {
    setSelectedScanner(scannerName);
    fetchScannerResults(scannerName);
  };

  const handleStockClick = (symbol) => navigate(`/stock/${symbol.replace('.NS', '')}`);
  const handlePatternCardClick = (pattern) => setChartPattern(pattern);
  const handleCloseChartModal = () => setChartPattern(null);

  // Close info popovers + screener dropdowns when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setInfoOpen(null);
      if (scannerInfoRef.current && !scannerInfoRef.current.contains(e.target)) {
        setScannerCatInfoOpen(null);
        setScannerSignalInfoOpen(null);
      }
      if (screenerTechRef.current && !screenerTechRef.current.contains(e.target)) setScreenerTechOpen(false);
      if (screenerPatternRef.current && !screenerPatternRef.current.contains(e.target)) setScreenerPatternOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleBack = () => {
    if (selectedScanner) { setSelectedScanner(null); setScannerResults([]); }
    else if (selectedCategory) { setSelectedCategory(null); }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: colors.accent, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  // Scanner Results View
  if (selectedScanner) {
    const categoryData = Object.values(categories).find(cat => cat.scanners.some(s => s.name === selectedScanner));
    const scannerData = categoryData?.scanners.find(s => s.name === selectedScanner);
    
    return (
      <div className="space-y-3" data-testid="scanner-results-page">
        <div className="flex items-center space-x-3">
          <button onClick={handleBack} className="app-card p-1.5 rounded-md" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }} data-testid="back-button">
            <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
          </button>
          <div>
            <h1 className="text-[17px] font-bold" style={{ color: colors.primaryText }}>{scannerData?.display || selectedScanner.replace(/_/g, ' ')}</h1>
            <p className="text-[12px]" style={{ color: colors.secondaryText }}>{totalResults} stocks found</p>
          </div>
        </div>

        <div className="app-card rounded-lg overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
          {resultsLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: colors.accent, borderTopColor: 'transparent' }} />
            </div>
          ) : scannerResults.length === 0 ? (
            <div className="text-center py-4">
              <Activity className="w-8 h-8 mx-auto mb-2" style={{ color: colors.secondaryText }} />
              <p className="text-[12px]" style={{ color: colors.secondaryText }}>No stocks found</p>
            </div>
          ) : (
            <div>
              {scannerResults.map((result, idx) => (
                <div key={idx} onClick={() => handleStockClick(result.symbol)}
                  className="flex items-center justify-between p-1.5 cursor-pointer border-b last:border-b-0"
                  style={{ borderColor: colors.border }}
                  data-testid={`scanner-result-${idx}`}>
                  <div className="flex-1">
                    <div className="flex items-center space-x-1.5">
                      <span className="font-semibold text-[12px]" style={{ color: colors.primaryText }}>{result.symbol.replace('.NS', '')}</span>
                      <span className="text-[12px] px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: result.signal_strength === 'strong' ? `${colors.profit}20` : `${colors.accent}20`, color: result.signal_strength === 'strong' ? colors.profit : colors.accent }}>
                        {result.signal_strength}
                      </span>
                    </div>
                    <p className="text-[12px]" style={{ color: colors.secondaryText }}>{result.company_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[12px]" style={{ color: colors.primaryText }}>{formatPrice(result.current_price)}</p>
                    <p className="text-[12px]" style={{ color: result.change_pct >= 0 ? colors.profit : colors.loss }}>
                      {result.change_pct >= 0 ? '+' : ''}{result.change_pct?.toFixed(2)}%
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 ml-2" style={{ color: colors.secondaryText }} />
                </div>
              ))}
            </div>
          )}
          
          {totalResults > 20 && (
            <div className="flex items-center justify-center space-x-3 p-1.5" style={{ borderTop: `1px solid ${colors.border}` }}>
              <button onClick={() => fetchScannerResults(selectedScanner, page - 1)} disabled={page === 1}
                className="px-1.5 py-0.5 rounded text-[12px] disabled:opacity-50" style={{ backgroundColor: colors.inputBg, color: colors.primaryText }}>
                Prev
              </button>
              <span className="text-[12px]" style={{ color: colors.secondaryText }}>Page {page}</span>
              <button onClick={() => fetchScannerResults(selectedScanner, page + 1)} disabled={page >= Math.ceil(totalResults / 20)}
                className="px-1.5 py-0.5 rounded text-[12px] disabled:opacity-50" style={{ backgroundColor: colors.inputBg, color: colors.primaryText }}>
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Scanner List (within category)
  if (selectedCategory) {
    const categoryData = categories[selectedCategory];
    const Icon = CATEGORY_ICONS[selectedCategory] || Activity;
    
    return (
      <div className="space-y-3" data-testid="scanner-list-page">
        <div className="flex items-center space-x-3">
          <button onClick={handleBack} className="app-card p-1.5 rounded-md" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }} data-testid="back-button">
            <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
          </button>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${colors.accent}20` }}>
              <Icon className="w-4 h-4" style={{ color: colors.accent }} />
            </div>
            <div>
              <h1 className="text-[17px] font-bold" style={{ color: colors.primaryText }}>{categoryData?.name}</h1>
              <p className="text-[12px]" style={{ color: colors.secondaryText }}>{categoryData?.scanners.length} scanners</p>
            </div>
          </div>
        </div>

        <div className="app-card rounded-lg overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
          {categoryData?.scanners.map((scanner, idx) => (
            <div key={idx} onClick={() => handleScannerSelect(scanner.name)}
              className="flex items-center justify-between p-1.5 cursor-pointer border-b last:border-b-0"
              style={{ borderColor: colors.border }}
              data-testid={`scanner-item-${scanner.name}`}>
              <span className="text-[12px] font-medium" style={{ color: colors.primaryText }}>{scanner.display}</span>
              <ChevronRight className="w-4 h-4" style={{ color: colors.secondaryText }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const handleScannerShare = async () => {
    if (!scannerCardRef.current || !chartSignal) return;
    setScannerCapturing(true);
    const sym = chartSignal.symbol?.replace('.NS', '') || 'signal';
    try {
      await captureAndShare(scannerCardRef.current, `${sym}-scanner.png`);
    } catch (e) { console.error('Share failed', e); }
    finally { setScannerCapturing(false); setScannerSocialOpen(false); }
  };

  // Main view
  return (
    <>
    <div className="space-y-2" data-testid="scanners-page">

      {/* Scanner signals learning nudge */}
      <button
        onClick={() => navigate('/learning?view=level&level=9&card=0')}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left"
        style={{ background: 'linear-gradient(135deg, #6366f120 0%, #8b5cf610 100%)', border: '1px solid #6366f135' }}>
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#6366f125' }}>
            <BookOpen className="w-3.5 h-3.5" style={{ color: '#818cf8' }} />
          </div>
          <div>
            <p className="text-[12px] font-bold" style={{ color: '#818cf8' }}>Signal or noise?</p>
            <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>Learn how to read scanner signals →</p>
          </div>
        </div>
        <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#6366f160' }} />
      </button>

      {/* -- Persistent Tab Bar -- */}
      <div className="flex items-center gap-2">
        <div data-tour="scanner-tab-bar" className="flex rounded-xl overflow-hidden flex-1" style={{ border: `1px solid ${colors.border}`, backgroundColor: colors.cardBg }}>
          {[
            { id: 'patterns', label: 'Patterns',  icon: BarChart3  },
            { id: 'scanners', label: 'Signals',   icon: Activity   },
            { id: 'filter',   label: 'Screener',  icon: ListFilter },
          ].map(({ id, label, icon: Icon }) => {
            const isActive = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[12px] font-bold transition-all"
                style={{
                  backgroundColor: isActive ? colors.accent : 'transparent',
                  color: isActive ? '#fff' : colors.secondaryText,
                }}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
        {scanLastDate && (
          <span className="text-[10px] font-bold px-2 py-1 rounded-lg flex-shrink-0"
            style={{ backgroundColor: '#6366f118', color: '#818cf8', border: '1px solid #6366f130' }}>
            {formatDate(scanLastDate + 'T00:00:00')}
          </span>
        )}
      </div>

      {/* -- Row 2: Timeframe chips -- */}
      {(activeTab === 'patterns' || activeTab === 'scanners') && (
        <div data-tour="scanner-timeframe" className="flex gap-1.5 overflow-x-auto items-center" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {activeTab === 'patterns' && ['swing', 'intraday'].map(mode => (
            <button key={mode} onClick={() => setPatternMode(mode)}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold capitalize whitespace-nowrap transition-all"
              style={{
                backgroundColor: patternMode === mode ? colors.accent : colors.cardBg,
                color: patternMode === mode ? '#fff' : colors.secondaryText,
                border: `1px solid ${patternMode === mode ? colors.accent : colors.border}`,
              }}>{mode}</button>
          ))}
          {activeTab === 'patterns' && patternMode === 'intraday' && ['5min', '15min'].map(tf => (
            <button key={tf} onClick={() => setIntradayTf(tf)}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap transition-all"
              style={{
                backgroundColor: intradayTf === tf ? `${colors.accent}25` : colors.cardBg,
                color: intradayTf === tf ? colors.accent : colors.secondaryText,
                border: `1px solid ${intradayTf === tf ? colors.accent : colors.border}`,
              }}>{tf}</button>
          ))}
          {activeTab === 'scanners' && ['swing', 'intraday'].map(id => (
            <button key={id} onClick={() => setScannerTimeframe(id)}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold capitalize whitespace-nowrap transition-all"
              style={{
                backgroundColor: scannerTimeframe === id ? colors.accent : colors.cardBg,
                color: scannerTimeframe === id ? '#fff' : colors.secondaryText,
                border: `1px solid ${scannerTimeframe === id ? colors.accent : colors.border}`,
              }}>{id}</button>
          ))}
          {activeTab === 'scanners' && scannerTimeframe === 'intraday' && ['5min', '15min'].map(tf => (
            <button key={tf} onClick={() => setScannerIntradayTf(tf)}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap transition-all"
              style={{
                backgroundColor: scannerIntradayTf === tf ? `${colors.accent}25` : colors.cardBg,
                color: scannerIntradayTf === tf ? colors.accent : colors.secondaryText,
                border: `1px solid ${scannerIntradayTf === tf ? colors.accent : colors.border}`,
              }}>{tf}</button>
          ))}
          {activeTab === 'scanners' && scannerTimeframe !== 'guide' && (
            <button onClick={() => fetchScannerSignals(scannerTimeframe, scannerIntradayTf)}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap ml-auto transition-all"
              style={{ backgroundColor: colors.cardBg, color: colors.accent, border: `1px solid ${colors.border}` }}>
              ↺ Refresh
            </button>
          )}
        </div>
      )}

      {/* -- Row 3: Pattern type / scanner category chips -- */}
      {activeTab === 'patterns' && Object.keys(groupedPatterns).length > 0 && (
        <div data-tour="scanner-pattern-filter" className="flex gap-1 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <button onClick={() => setActivePatternFilter('ALL')}
            className="flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap"
            style={{
              backgroundColor: activePatternFilter === 'ALL' ? colors.accent : colors.inputBg,
              color: activePatternFilter === 'ALL' ? '#fff' : colors.primaryText,
              borderWidth: '1px', borderColor: activePatternFilter === 'ALL' ? colors.accent : colors.border,
            }}>All</button>
          {Object.entries(groupedPatterns).map(([patternType, patternList]) => {
            const info = PATTERN_INFO[patternType] || { label: patternType, color: colors.accent };
            const isActive = activePatternFilter === patternType;
            return (
              <button key={patternType}
                onClick={() => setActivePatternFilter(isActive ? 'ALL' : patternType)}
                className="flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap"
                style={{
                  backgroundColor: isActive ? `${info.color}25` : colors.inputBg,
                  color: isActive ? info.color : colors.primaryText,
                  borderWidth: '1px', borderColor: isActive ? info.color : colors.border,
                }}>
                {info.label} <span style={{ opacity: 0.6 }}>{patternList.length}</span>
              </button>
            );
          })}
        </div>
      )}
      {activeTab === 'scanners' && scannerTimeframe !== 'guide' && Object.keys(scannerSignalsGrouped).length > 0 && (
        <div className="flex gap-1 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <button onClick={() => setActiveScannerCategory('ALL')}
            className="flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap"
            style={{
              backgroundColor: activeScannerCategory === 'ALL' ? colors.accent : colors.inputBg,
              color: activeScannerCategory === 'ALL' ? '#fff' : colors.primaryText,
              borderWidth: '1px', borderColor: activeScannerCategory === 'ALL' ? colors.accent : colors.border,
            }}>All</button>
          {Object.entries(scannerSignalsGrouped).map(([cat, sigs]) => {
            const isActive = activeScannerCategory === cat;
            const catMeta = SCANNER_CATEGORY_LABELS[cat];
            const label = catMeta?.label || cat;
            return (
              <button key={cat}
                onClick={() => setActiveScannerCategory(isActive ? 'ALL' : cat)}
                className="flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap"
                style={{
                  backgroundColor: isActive ? `${colors.accent}25` : colors.inputBg,
                  color: isActive ? colors.accent : colors.primaryText,
                  borderWidth: '1px', borderColor: isActive ? colors.accent : colors.border,
                }}>
                {label} <span style={{ opacity: 0.6 }}>{sigs.length}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Scanners Tab Content — grouped signal cards */}
      {activeTab === 'scanners' && (
        <div className="space-y-2">

          {/* ── All Scanners Guide ── */}
          {scannerTimeframe === 'guide' && (
            <div className="space-y-2">
              <p className="text-[12px] pb-0.5" style={{ color: colors.secondaryText }}>
                27 scanners across 6 categories. No live signals — reference guide only.
              </p>

              {Object.entries(SCANNER_CATEGORY_LABELS).map(([catKey, catMeta]) => {
                const CatIcon = catMeta.icon;
                const catInfo = SCANNER_CATEGORY_INFO[catKey];
                const scannersInCat = Object.entries(SCANNER_INFO).filter(
                  ([name]) => SCANNER_CATEGORY_MAP[name] === catKey
                );
                const isCatCollapsed = collapsedGuideCategories.has(catKey);

                return (
                  <div key={catKey} className="app-card rounded-lg overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>

                    {/* ── Category header (clickable) ── */}
                    <button
                      className="w-full flex items-center justify-between p-1.5 text-left"
                      onClick={() => {
                        const next = new Set(collapsedGuideCategories);
                        if (next.has(catKey)) next.delete(catKey); else next.add(catKey);
                        setCollapsedGuideCategories(next);
                      }}
                    >
                      <div className="flex items-center space-x-2">
                        <CatIcon className="w-4 h-4" style={{ color: colors.accent }} />
                        <span className="font-semibold text-[12px]" style={{ color: colors.primaryText }}>{catMeta.label}</span>
                        <span className="px-1 py-0.5 rounded text-[12px]" style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>
                          {scannersInCat.length} scanners
                        </span>
                      </div>
                      <ChevronRight className="w-4 h-4 transition-transform flex-shrink-0"
                        style={{ color: colors.secondaryText, transform: isCatCollapsed ? 'rotate(0deg)' : 'rotate(90deg)' }} />
                    </button>

                    {/* ── Category body ── */}
                    {!isCatCollapsed && (
                      <div className="border-t" style={{ borderColor: colors.border }}>
                        {/* 2-line category explanation */}
                        {catInfo && (
                          <div className="px-1 py-0.5 space-y-1" style={{ backgroundColor: `${colors.accent}08` }}>
                            <p className="text-[12px]" style={{ color: colors.secondaryText }}>{catInfo.explanation}</p>
                            <p className="text-[12px]" style={{ color: colors.accent }}>⚡ {catInfo.watch}</p>
                          </div>
                        )}

                        {/* ── Scanner rows (nested collapsibles) ── */}
                        <div className="divide-y" style={{ borderColor: colors.border }}>
                          {scannersInCat.map(([scannerName, info]) => {
                            const detail = SCANNER_DETAIL[scannerName];
                            const dirColor = info.direction === 'bullish' ? '#16C784' : info.direction === 'bearish' ? '#ef4444' : '#6366F1';
                            const isScannerCollapsed = collapsedGuideScanners.has(scannerName);

                            return (
                              <div key={scannerName}>
                                {/* Scanner header row */}
                                <button
                                  className="w-full flex items-center justify-between px-1 py-0.5 text-left"
                                  onClick={() => {
                                    const next = new Set(collapsedGuideScanners);
                                    if (next.has(scannerName)) next.delete(scannerName); else next.add(scannerName);
                                    setCollapsedGuideScanners(next);
                                  }}
                                >
                                  <div className="flex items-center space-x-2">
                                    <ChevronRight className="w-3 h-3 flex-shrink-0 transition-transform"
                                      style={{ color: colors.secondaryText, transform: isScannerCollapsed ? 'rotate(0deg)' : 'rotate(90deg)' }} />
                                    <span className="text-[12px] font-medium" style={{ color: colors.primaryText }}>{info.label}</span>
                                    <span className="px-1.5 py-0.5 rounded text-[12px] font-bold capitalize"
                                      style={{ backgroundColor: `${dirColor}20`, color: dirColor }}>
                                      {info.direction}
                                    </span>
                                  </div>
                                  <span className="text-[12px]" style={{ color: colors.secondaryText }}>{info.description}</span>
                                </button>

                                {/* Scanner detail body */}
                                {!isScannerCollapsed && detail && (
                                  <div className="px-1 pb-2 space-y-3 border-t" style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}>
                                    {/* Explanation */}
                                    <div className="pt-0.5.5">
                                      <p className="text-[12px] font-semibold uppercase tracking-wide mb-1" style={{ color: colors.accent }}>What it detects</p>
                                      <p className="text-[12px]" style={{ color: colors.secondaryText }}>{detail.explanation}</p>
                                    </div>

                                    {/* Criteria / Properties */}
                                    {detail.values && Object.keys(detail.values).length > 0 && (
                                      <div>
                                        <p className="text-[12px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: colors.accent }}>Criteria &amp; Properties</p>
                                        <div className="space-y-1">
                                          {Object.entries(detail.values).map(([k, v]) => (
                                            <div key={k} className="flex text-[12px]" style={{ gap: '6px' }}>
                                              <span className="font-semibold flex-shrink-0" style={{ color: colors.primaryText, minWidth: '120px' }}>{k}</span>
                                              <span style={{ color: colors.secondaryText }}>{v}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Example / Action */}
                                    <div className="rounded px-1.5 py-0.5" style={{ backgroundColor: `${dirColor}10`, borderWidth: '1px', borderColor: `${dirColor}30` }}>
                                      <p className="text-[12px] font-semibold uppercase tracking-wide mb-1" style={{ color: dirColor }}>How to act</p>
                                      <p className="text-[12px]" style={{ color: colors.primaryText }}>{detail.action}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Confluence mode toggle + scanner multi-select */}
          <div data-tour="scanner-confluence" className="rounded-xl overflow-hidden" style={{ border: `1px solid ${confluenceMode ? colors.accent + '50' : colors.border}` }}>
            <div className="flex items-center justify-between px-3 py-2.5" style={{ backgroundColor: confluenceMode ? `${colors.accent}15` : colors.cardBg }}>
              <div>
                <p className="text-[12px] font-bold" style={{ color: colors.primaryText }}>Confluence Filter</p>
                <p className="text-[11px] mt-0.5" style={{ color: colors.secondaryText }}>
                  Pick 2+ scanners — see stocks that triggered in all
                </p>
              </div>
              <button onClick={() => { setConfluenceMode(!confluenceMode); setSelectedScanners(new Set()); }}
                className="px-3 py-1.5 rounded-full text-[11px] font-bold transition-all"
                style={{
                  backgroundColor: confluenceMode ? colors.accent : colors.cardBg,
                  color: confluenceMode ? '#fff' : colors.secondaryText,
                  border: `1px solid ${confluenceMode ? colors.accent : colors.border}`,
                }}>
                {confluenceMode ? '✓ On' : 'Off'}
              </button>
            </div>
            {confluenceMode && (
              <div className="flex flex-wrap gap-1.5 px-3 py-2.5" style={{ borderTop: `1px solid ${colors.border}`, backgroundColor: colors.cardBg }}>
                {scannerSignals.length > 0 && [...new Set(scannerSignals.map(s => s.scanner_name))].map(name => {
                  const info = SCANNER_INFO[name] || { label: name, color: colors.accent };
                  const sel = selectedScanners.has(name);
                  return (
                    <button key={name} onClick={() => {
                      const next = new Set(selectedScanners);
                      sel ? next.delete(name) : next.add(name);
                      setSelectedScanners(next);
                    }}
                    className="px-2.5 py-1 rounded-full text-[11px] font-bold transition-all"
                    style={{
                      backgroundColor: sel ? info.color : colors.inputBg,
                      color: sel ? '#fff' : colors.secondaryText,
                      border: `1px solid ${sel ? info.color : colors.border}`,
                    }}>
                      {info.label}
                    </button>
                  );
                })}
                {selectedScanners.size >= 2 && (() => {
                  const scannerArrays = [...selectedScanners].map(name =>
                    new Set(scannerSignals.filter(s => s.scanner_name === name).map(s => s.symbol))
                  );
                  const intersection = [...scannerArrays[0]].filter(sym => scannerArrays.every(set => set.has(sym)));
                  return (
                    <p className="w-full text-[11px] font-bold mt-1" style={{ color: colors.accent }}>
                      {intersection.length} stock{intersection.length !== 1 ? 's' : ''} match all {selectedScanners.size} scanners
                    </p>
                  );
                })()}
              </div>
            )}
          </div>

          {scannerSignalsLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: colors.accent, borderTopColor: 'transparent' }} />
            </div>
          ) : (() => {
            // Derive the signal list to show
            let displaySignals = scannerSignals;

            // Confluence filter: only symbols in ALL selected scanners
            if (confluenceMode && selectedScanners.size >= 2) {
              const scannerArrays = [...selectedScanners].map(name =>
                new Set(scannerSignals.filter(s => s.scanner_name === name).map(s => s.symbol))
              );
              const intersection = new Set([...scannerArrays[0]].filter(sym => scannerArrays.every(set => set.has(sym))));
              displaySignals = scannerSignals.filter(s => intersection.has(s.symbol));
            } else if (confluenceMode && selectedScanners.size === 1) {
              displaySignals = scannerSignals.filter(s => selectedScanners.has(s.scanner_name));
            }

            // Category filter
            const filteredGrouped = {};
            displaySignals.forEach(sig => {
              const cat = SCANNER_CATEGORY_MAP[sig.scanner_name] || 'OTHER';
              if (activeScannerCategory !== 'ALL' && cat !== activeScannerCategory) return;
              if (!filteredGrouped[cat]) filteredGrouped[cat] = [];
              filteredGrouped[cat].push(sig);
            });

            if (Object.keys(filteredGrouped).length === 0) return (
              <div className="app-card text-center py-4 rounded-lg" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
                <Zap className="w-12 h-12 mx-auto mb-4" style={{ color: colors.secondaryText }} />
                <p className="font-medium" style={{ color: colors.primaryText }}>
                  {scannerSignals.length === 0 ? 'No signals triggered yet' : 'No signals match this filter'}
                </p>
                <p className="text-[12px] mt-1" style={{ color: colors.secondaryText }}>
                  {scannerSignals.length === 0
                    ? (scannerTimeframe === 'intraday' ? 'Intraday scanners run every 5/15 min during market hours.' : 'Scanners run every 30 min during market hours.')
                    : 'Try selecting a different category or timeframe.'}
                </p>
              </div>
            );

            return (
              <>
                <p className="text-[12px]" style={{ color: colors.secondaryText }}>
                  {displaySignals.length} signals {confluenceMode && selectedScanners.size >= 2 ? `(confluence: ${selectedScanners.size} scanners)` : ''}
                </p>
                {/* Category sections */}
                {Object.entries(filteredGrouped).map(([catKey, signals]) => {
                const catMeta = SCANNER_CATEGORY_LABELS[catKey] || { label: catKey, icon: Activity };
                const CatIcon = catMeta.icon;
                const isCatCollapsed = collapsedScanners.has(catKey);
                const toggleCatCollapse = () => {
                  const next = new Set(collapsedScanners);
                  isCatCollapsed ? next.delete(catKey) : next.add(catKey);
                  setCollapsedScanners(next);
                };
                return (
                  <div key={catKey} className="app-card rounded-lg overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
                    {/* Category header — click to collapse */}
                    <div
                      className="flex items-center space-x-2 px-3 py-2.5 cursor-pointer select-none relative"
                      style={{ borderBottom: isCatCollapsed ? 'none' : `1px solid ${colors.border}` }}
                      onClick={toggleCatCollapse}
                      ref={scannerCatInfoOpen === catKey ? scannerInfoRef : null}
                    >
                      <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 transition-transform"
                        style={{ color: colors.secondaryText, transform: isCatCollapsed ? 'rotate(0deg)' : 'rotate(90deg)' }} />
                      <CatIcon className="w-4 h-4" style={{ color: colors.accent }} />
                      <h3 className="font-semibold text-[12px]" style={{ color: colors.primaryText }}>{catMeta.label}</h3>
                      <span className="px-1 py-0.5 rounded text-[12px]" style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>
                        {signals.length}
                      </span>
                      {SCANNER_CATEGORY_INFO[catKey] && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setScannerCatInfoOpen(scannerCatInfoOpen === catKey ? null : catKey); setScannerSignalInfoOpen(null); }}
                          className="ml-1 p-0.5 rounded-full"
                          style={{ color: colors.secondaryText }}
                        >
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {scannerCatInfoOpen === catKey && SCANNER_CATEGORY_INFO[catKey] && (
                        <div className="app-card absolute left-0 top-full mt-1 z-50 w-80 rounded-lg p-1.5 shadow-xl text-[12px] space-y-2"
                          style={{ backgroundColor: colors.cardBg, borderWidth: '1px', borderColor: colors.border }}>
                          <div className="flex items-center space-x-1.5 mb-1">
                            <CatIcon className="w-3.5 h-3.5" style={{ color: colors.accent }} />
                            <span className="font-semibold" style={{ color: colors.accent }}>{SCANNER_CATEGORY_INFO[catKey].label || catMeta.label}</span>
                          </div>
                          <p style={{ color: colors.primaryText }}>{SCANNER_CATEGORY_INFO[catKey].explanation}</p>
                          <div className="pt-0.5" style={{ borderTop: `1px solid ${colors.border}` }}>
                            <p className="font-medium mb-0.5" style={{ color: colors.secondaryText }}>What to watch:</p>
                            <p style={{ color: colors.secondaryText }}>{SCANNER_CATEGORY_INFO[catKey].watch}</p>
                          </div>
                          <div className="pt-0.5" style={{ borderTop: `1px solid ${colors.border}` }}>
                            <p className="text-[12px]" style={{ color: colors.secondaryText }}>
                              <span style={{ color: colors.profit }}>B (Buy)</span> = bullish scanner direction &nbsp;·&nbsp;
                              <span style={{ color: colors.loss }}>S (Sell)</span> = bearish &nbsp;·&nbsp;
                              Both = neutral (you pick direction based on context)
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Signal cards grid */}
                    {!isCatCollapsed && <div data-tour="scanner-signal-cards" className="p-3 grid grid-cols-2 md:grid-cols-5 gap-3">
                      {signals.map((signal, idx) => {
                        const info = SCANNER_INFO[signal.scanner_name] || { label: signal.scanner_name, color: colors.accent, direction: 'neutral', description: '' };
                        const sigStatus = getSignalStatus(signal.triggered_at);
                        const live      = socketPrices[signal.symbol];
                        const isBullish = info.direction === 'bullish';
                        const isBearish = info.direction === 'bearish';
                        const dirColor  = isBullish ? colors.profit : isBearish ? colors.loss : colors.accent;
                        const isStrong  = (signal.strengthPct ?? 0) >= 75;

                        // Price % change vs prev_close
                        const livePrice  = live?.price;
                        const prevClose  = signal.prev_close;
                        const changePct  = (prevClose && livePrice) ? ((livePrice - prevClose) / prevClose) * 100 : null;
                        const changeUp   = changePct !== null && changePct >= 0;

                        // Quick risk/qty estimate (1.5% SL from live price)
                        const slEst      = livePrice ? (isBearish ? livePrice * 1.015 : livePrice * 0.985) : null;
                        const tgtEst     = livePrice ? (isBearish ? livePrice * 0.97  : livePrice * 1.03)  : null;
                        const metrics    = (livePrice && slEst) ? computeOptionMetrics(livePrice, slEst, tgtEst, capital, riskPct) : null;

                        // Dynamic signal description
                        const desc = getDynamicDescription(signal, info, livePrice);

                        return (
                          <div
                            key={idx}
                            onClick={() => setChartSignal(signal)}
                            className="rounded-xl cursor-pointer transition-all overflow-hidden"
                            style={{
                              backgroundColor: colors.cardBg,
                              border: `1px solid ${isStrong ? dirColor + '60' : colors.border}`,
                              boxShadow: isStrong ? `0 0 14px ${dirColor}20` : 'none',
                            }}
                          >
                            {/* Colored top strip */}
                            <div className="px-2.5 py-1.5 flex items-center justify-between" style={{ backgroundColor: `${dirColor}15`, borderBottom: `1px solid ${dirColor}20` }}>
                              <span className="font-black text-[13px]" style={{ color: colors.primaryText }}>
                                {signal.symbol.replace('.NS', '')}
                              </span>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {isStrong && (
                                  <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full"
                                    style={{ backgroundColor: `${dirColor}30`, color: dirColor }}>
                                    TOP
                                  </span>
                                )}
                                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                                  style={{ backgroundColor: sigStatus === 'fresh' ? 'rgba(22,199,132,0.2)' : sigStatus === 'active' ? 'rgba(99,102,241,0.2)' : 'rgba(107,114,128,0.15)',
                                           color: sigStatus === 'fresh' ? '#16C784' : sigStatus === 'active' ? '#818cf8' : '#6b7280' }}>
                                  {formatSignalAge(signal.triggered_at) || sigStatus}
                                </span>
                              </div>
                            </div>

                            <div className="p-2.5">
                              {/* Signal name + direction + info */}
                              <div className="flex items-center gap-1 mb-1.5 relative"
                                ref={scannerSignalInfoOpen === `${signal.scanner_name}-${signal.symbol}` ? scannerInfoRef : null}>
                                <span className="text-[11px] font-bold truncate" style={{ color: dirColor }}>{info.label}</span>
                                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold capitalize flex-shrink-0"
                                  style={{ backgroundColor: `${dirColor}20`, color: dirColor }}>
                                  {info.direction}
                                </span>
                                {SCANNER_DETAIL[signal.scanner_name] && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); const k = `${signal.scanner_name}-${signal.symbol}`; setScannerSignalInfoOpen(scannerSignalInfoOpen === k ? null : k); setScannerCatInfoOpen(null); }}
                                    className="p-0.5 flex-shrink-0 ml-auto"
                                    style={{ color: colors.secondaryText }}
                                  >
                                    <Info className="w-3 h-3" />
                                  </button>
                                )}
                                {scannerSignalInfoOpen === `${signal.scanner_name}-${signal.symbol}` && SCANNER_DETAIL[signal.scanner_name] && (
                                  <div className="app-card absolute left-0 top-full mt-1 z-50 w-72 rounded-xl p-3 shadow-xl text-[12px] space-y-2"
                                    style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
                                    <p className="font-bold" style={{ color: dirColor }}>{info.label}</p>
                                    <p style={{ color: colors.primaryText }}>{SCANNER_DETAIL[signal.scanner_name].explanation}</p>
                                    {SCANNER_DETAIL[signal.scanner_name].values && (
                                      <div className="pt-2 space-y-1" style={{ borderTop: `1px solid ${colors.border}` }}>
                                        {Object.entries(SCANNER_DETAIL[signal.scanner_name].values).map(([k, v]) => (
                                          <div key={k}>
                                            <span className="font-semibold" style={{ color: colors.accent }}>{k}: </span>
                                            <span style={{ color: colors.secondaryText }}>{v}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    <div className="pt-2" style={{ borderTop: `1px solid ${colors.border}` }}>
                                      <p className="font-semibold mb-0.5" style={{ color: colors.secondaryText }}>What to do:</p>
                                      <p style={{ color: colors.primaryText }}>{SCANNER_DETAIL[signal.scanner_name].action}</p>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Description */}
                              <p className="text-[11px] leading-snug mb-2 line-clamp-2" style={{ color: colors.secondaryText }}>{desc}</p>

                              {/* Price + change + strength */}
                              <div className="flex items-center justify-between mb-2.5">
                                <div className="flex items-baseline gap-1">
                                  {livePrice ? (
                                    <>
                                      <span className="font-bold text-[12px]" style={{ color: colors.primaryText }}>{formatPrice(livePrice)}</span>
                                      {changePct !== null && (
                                        <span className="text-[11px] font-semibold" style={{ color: changeUp ? colors.profit : colors.loss }}>
                                          {changeUp ? '+' : ''}{changePct.toFixed(1)}%
                                        </span>
                                      )}
                                    </>
                                  ) : null}
                                </div>
                                <div className="flex items-center gap-1">
                                  <div className="w-12 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: colors.border }}>
                                    <div className="h-full rounded-full transition-all" style={{ width: `${signal.strengthPct ?? 50}%`, backgroundColor: dirColor }} />
                                  </div>
                                  <span className="text-[10px] font-bold" style={{ color: dirColor }}>{signal.strengthPct ?? 50}%</span>
                                </div>
                              </div>

                              {/* View Chart button */}
                              <div onClick={e => e.stopPropagation()}>
                                <button
                                  onClick={() => setChartSignal(signal)}
                                  className="w-full py-1.5 rounded-lg text-[11px] font-bold transition-all"
                                  style={{ backgroundColor: `${dirColor}15`, color: dirColor, border: `1px solid ${dirColor}30` }}
                                >
                                  View Chart →
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>}
                  </div>
                );
              })}
            </>
            );
          })()}
        </div>
      )}

      {/* Chart Patterns Tab Content */}
      {activeTab === 'patterns' && (
        <div className="space-y-2">
          {patternsLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: colors.accent, borderTopColor: 'transparent' }} />
            </div>
          ) : Object.keys(groupedPatterns).length === 0 ? (
            <div className="app-card text-center py-6 rounded-lg" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
              <Layers className="w-12 h-12 mx-auto mb-4" style={{ color: colors.secondaryText }} />
              <p className="font-medium" style={{ color: colors.primaryText }}>No patterns detected</p>
              <p className="text-[12px] mt-1" style={{ color: colors.secondaryText }}>
                Patterns are scanned daily. Check back later.
              </p>
            </div>
          ) : (
            <>
              {/* Pattern Categories — filtered */}
              {Object.entries(groupedPatterns)
                .filter(([patternType]) => activePatternFilter === 'ALL' || activePatternFilter === patternType)
                .map(([patternType, patternList]) => {
                const info = PATTERN_INFO[patternType] || { label: patternType, color: colors.accent, icon: Activity };
                const Icon = info.icon;
                const isCollapsed = collapsedPatterns.has(patternType);
                const toggleCollapse = () => {
                  const next = new Set(collapsedPatterns);
                  isCollapsed ? next.delete(patternType) : next.add(patternType);
                  setCollapsedPatterns(next);
                };

                return (
                  <div key={patternType} className="app-card rounded-lg overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
                    {/* Pattern Type Header — click to collapse */}
                    <div
                      className="flex items-center space-x-2 px-3 py-2.5 cursor-pointer select-none relative"
                      style={{ borderBottom: isCollapsed ? 'none' : `1px solid ${colors.border}` }}
                      onClick={toggleCollapse}
                      ref={infoOpen === patternType ? infoRef : null}
                    >
                      <ChevronRight className="w-3.5 h-3.5 transition-transform flex-shrink-0"
                        style={{ color: colors.secondaryText, transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)' }} />
                      <Icon className="w-4 h-4" style={{ color: info.color }} />
                      <h3 className="font-semibold text-[12px]" style={{ color: colors.primaryText }}>{info.label}</h3>
                      <span className="px-1 py-0.5 rounded text-[12px]" style={{ backgroundColor: `${info.color}20`, color: info.color }}>
                        {patternList.length}
                      </span>
                      {/* Info icon */}
                      {info.explanation && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setInfoOpen(infoOpen === patternType ? null : patternType); }}
                          className="ml-1 p-0.5 rounded-full"
                          style={{ color: colors.secondaryText }}
                        >
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {/* Info popover */}
                      {infoOpen === patternType && info.explanation && (
                        <div className="app-card absolute left-0 top-full mt-1 z-50 w-80 rounded-lg p-1.5 shadow-xl text-[12px] space-y-2"
                          style={{ backgroundColor: colors.cardBg, borderWidth: '1px', borderColor: colors.border }}>
                          <div className="flex items-center space-x-1.5 mb-1">
                            <Icon className="w-3.5 h-3.5" style={{ color: info.color }} />
                            <span className="font-semibold" style={{ color: info.color }}>{info.description}</span>
                          </div>
                          <p style={{ color: colors.primaryText }}>{info.explanation}</p>
                          {info.values && (
                            <div className="pt-0.5 space-y-1" style={{ borderTop: `1px solid ${colors.border}` }}>
                              {Object.entries(info.values).map(([k, v]) => (
                                <div key={k}>
                                  <span className="font-medium" style={{ color: colors.accent }}>{k}: </span>
                                  <span style={{ color: colors.secondaryText }}>{v}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Status badge legend */}
                          <div className="pt-0.5 space-y-1" style={{ borderTop: `1px solid ${colors.border}` }}>
                            <p className="font-medium mb-1" style={{ color: colors.secondaryText }}>Signal status badges:</p>
                            {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'EXHAUSTED').map(([k, s]) => (
                              <div key={k} className="flex items-center space-x-1.5">
                                <span className="px-1.5 py-0.5 rounded text-[12px] font-bold" style={{ backgroundColor: s.bg, color: s.color }}>{s.label}</span>
                                <span style={{ color: colors.secondaryText }}>
                                  {k === 'DETECTED' ? 'Pattern forming, no breakout yet' :
                                   k === 'CONFIRMED' ? 'Breakout just happened (1–4 candles)' :
                                   'Breakout confirmed, trade window open'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Pattern Stocks Grid */}
                    {!isCollapsed && <div data-tour="scanner-pattern-cards" className="p-3 grid grid-cols-2 md:grid-cols-5 gap-3">
                      {patternList.map((pattern, idx) => {
                        const live = socketPrices[pattern.symbol];
                        const detected = pattern.price_level;
                        const detectedPct = live?.price && detected
                          ? (((live.price - detected) / detected) * 100).toFixed(1)
                          : null;
                        const statusCfg = STATUS_CONFIG[pattern.pattern_status] || STATUS_CONFIG.DETECTED;
                        const isDetected = pattern.pattern_status === 'DETECTED';
                        return (
                            <div
                              key={idx}
                              onClick={() => handlePatternCardClick(pattern)}
                              className="p-3 rounded-lg cursor-pointer transition-all hover:scale-[1.01]"
                              style={{
                                backgroundColor: colors.inputBg,
                                borderWidth: '1px',
                                borderColor: colors.border,
                              }}
                              data-testid={`pattern-${patternType}-${idx}`}
                            >
                              {/* Header: name + status badge + confidence */}
                              <div className="flex items-center justify-between mb-1.5 gap-1">
                                <span className="font-bold text-[13px] truncate" style={{ color: colors.primaryText }}>
                                  {pattern.symbol.replace('.NS', '')}
                                </span>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {pattern.pattern_status && (
                                    <span className="px-1 py-0.5 rounded text-[10px] font-bold"
                                      style={{ backgroundColor: statusCfg.bg, color: statusCfg.color }}>
                                      {statusCfg.label}
                                    </span>
                                  )}
                                  <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${pattern.confidence === 'high' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}`}>
                                    {pattern.confidence}
                                  </span>
                                </div>
                              </div>
                              {/* Signal + detected age */}
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-[12px] font-medium truncate mr-1" style={{ color: info.color }}>
                                  {info.description || info.label}
                                </p>
                                {pattern.detected_at && (
                                  <span className="text-[10px] flex-shrink-0" style={{ color: colors.secondaryText }}>
                                    {formatDetectedAge(pattern.detected_at)}
                                  </span>
                                )}
                              </div>
                              {/* Price rows */}
                              <div className="space-y-1">
                                {pattern.neckline != null ? (
                                  <>
                                    <div className="flex items-center justify-between text-[12px]">
                                      <span style={{ color: colors.secondaryText }}>Level</span>
                                      <span style={{ color: colors.primaryText }}>{detected != null ? formatPrice(detected) : '…'}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-[12px]">
                                      <span style={{ color: colors.secondaryText }}>Neck</span>
                                      <span style={{ color: colors.accent }}>{formatPrice(pattern.neckline)}</span>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex items-center justify-between text-[12px]">
                                    <span style={{ color: colors.secondaryText }}>Key Level</span>
                                    <span style={{ color: colors.accent }}>{detected != null ? formatPrice(detected) : '…'}</span>
                                  </div>
                                )}
                                {isDetected && pattern.proximity_pct != null && (
                                  <div className="flex items-center justify-between text-[12px]">
                                    <span style={{ color: colors.secondaryText }}>Proximity</span>
                                    <span style={{ color: pattern.proximity_pct < 2 ? '#16C784' : pattern.proximity_pct < 5 ? '#eab308' : colors.secondaryText }}>
                                      {pattern.proximity_pct.toFixed(1)}%
                                    </span>
                                  </div>
                                )}
                                {!isDetected && pattern.distance_from_neckline != null && (
                                  <div className="flex items-center justify-between text-[12px]">
                                    <span style={{ color: colors.secondaryText }}>Dist.</span>
                                    <span style={{ color: colors.secondaryText }}>{pattern.distance_from_neckline.toFixed(1)}%</span>
                                  </div>
                                )}
                                {!isDetected && pattern.candles_since_breakout > 0 && (
                                  <div className="flex items-center justify-between text-[12px]">
                                    <span style={{ color: colors.secondaryText }}>Breakout</span>
                                    <span style={{ color: colors.secondaryText }}>{pattern.candles_since_breakout}c</span>
                                  </div>
                                )}
                              </div>
                            </div>
                        );
                      })}
                    </div>}
                  </div>
                );
              })}

            </>
          )}
        </div>
      )}

      {/* Stock Filter Tab Content */}
      {activeTab === 'filter' && (() => {
        // ── Compute filtered + sorted results ──
        const norm = s => (s || '').replace('.NS', '').toUpperCase();

        // Build lookup sets for tech filters
        const techSets = {};
        screenerTechFilters.forEach(name => {
          techSets[name] = new Set(screenerScanResults.filter(r => r.scanner_name === name).map(r => norm(r.symbol)));
        });
        // Build lookup sets for pattern filters
        const patternSets = {};
        screenerPatternFilters.forEach(ptype => {
          patternSets[ptype] = new Set(screenerPatterns.filter(p => p.pattern_type === ptype).map(p => norm(p.symbol)));
        });

        let results = screenerStocks.filter(s => {
          const sym = norm(s.symbol);
          const price = s.current_price;

          if (screenerSearch) {
            const q = screenerSearch.toLowerCase();
            if (!s.symbol.toLowerCase().includes(q) && !(s.company_name || '').toLowerCase().includes(q)) return false;
          }
          if (screenerSector && s.sector !== screenerSector) return false;
          if (screenerPriceRange && price) {
            if (screenerPriceRange === '<500'      && price >= 500)  return false;
            if (screenerPriceRange === '500-1000'  && (price < 500  || price >= 1000)) return false;
            if (screenerPriceRange === '1000-3000' && (price < 1000 || price >= 3000)) return false;
            if (screenerPriceRange === '>3000'     && price < 3000)  return false;
          }
          if (screenerTechFilters.size > 0 && ![...screenerTechFilters].every(name => techSets[name].has(sym))) return false;
          if (screenerPatternFilters.size > 0 && ![...screenerPatternFilters].every(pt => patternSets[pt].has(sym))) return false;
          return true;
        });

        results.sort((a, b) => {
          let av, bv;
          if (screenerSortBy === 'price')     { av = a.current_price || 0; bv = b.current_price || 0; }
          else if (screenerSortBy === 'change') { av = a.change_pct || 0; bv = b.change_pct || 0; }
          else if (screenerSortBy === 'mcap')   { av = a.market_cap || 0; bv = b.market_cap || 0; }
          else                                  { av = (a.company_name || '').toLowerCase(); bv = (b.company_name || '').toLowerCase(); }
          if (av === bv) return 0;
          return screenerSortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
        });

        const allSectors = [...new Set(screenerStocks.map(s => s.sector).filter(Boolean))].sort();
        const activeFilterCount = (screenerSector ? 1 : 0) + (screenerPriceRange ? 1 : 0) + screenerTechFilters.size + screenerPatternFilters.size;

        return (
          <div className="space-y-2">

            {/* Row 1: Search + Sector + Price */}
            <div className="flex gap-1.5 items-center">
              <input
                type="text" placeholder="Search symbol or company…" value={screenerSearch}
                onChange={e => setScreenerSearch(e.target.value)}
                className="flex-1 min-w-0 px-1 py-0.5 rounded text-[12px] outline-none"
                style={{ backgroundColor: colors.inputBg, color: colors.primaryText, borderWidth: '1px', borderColor: colors.border }}
              />
              <select value={screenerSector || ''} onChange={e => setScreenerSector(e.target.value || null)}
                className="px-1 py-0.5 rounded text-[12px] outline-none flex-shrink-0"
                style={{ backgroundColor: colors.inputBg, color: screenerSector ? colors.accent : colors.secondaryText, borderWidth: '1px', borderColor: screenerSector ? colors.accent : colors.border }}>
                <option value="">All Sectors</option>
                {allSectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={screenerPriceRange || ''} onChange={e => setScreenerPriceRange(e.target.value || null)}
                className="px-1 py-0.5 rounded text-[12px] outline-none flex-shrink-0"
                style={{ backgroundColor: colors.inputBg, color: screenerPriceRange ? colors.accent : colors.secondaryText, borderWidth: '1px', borderColor: screenerPriceRange ? colors.accent : colors.border }}>
                <option value="">All Prices</option>
                <option value="<500">Under ₹500</option>
                <option value="500-1000">₹500–₹1k</option>
                <option value="1000-3000">₹1k–₹3k</option>
                <option value=">3000">Above ₹3k</option>
              </select>
            </div>

            {/* Row 2a: Sort pills (scrollable — no dropdowns here to avoid overflow clip) */}
            <div className="flex items-center gap-1 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {[['name','Name'],['price','Price'],['change','Chg%'],['mcap','MCap']].map(([val, label]) => (
                <button key={val} onClick={() => setScreenerSortBy(val)}
                  className="flex-shrink-0 px-1 py-0.5 rounded text-[12px] font-semibold"
                  style={{ backgroundColor: screenerSortBy === val ? colors.accent : colors.inputBg, color: screenerSortBy === val ? '#fff' : colors.secondaryText, borderWidth: '1px', borderColor: screenerSortBy === val ? colors.accent : colors.border }}>
                  {label}
                </button>
              ))}
              <button onClick={() => setScreenerSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                className="flex-shrink-0 px-1 py-0.5 rounded text-[12px] font-bold"
                style={{ backgroundColor: colors.inputBg, color: colors.accent, borderWidth: '1px', borderColor: colors.border }}>
                {screenerSortDir === 'asc' ? '?' : '—'}
              </button>
              {/* Results count */}
              <span className="flex-shrink-0 ml-auto text-[12px]" style={{ color: colors.secondaryText }}>
                {screenerLoading ? '…' : `${results.length}/${screenerStocks.length}`}
              </span>
            </div>

            {/* Row 2b: Scanner ? Pattern ? Swing/5min/15min ? Clear (NOT inside overflow-x-auto so dropdowns aren't clipped) */}
            <div className="flex items-center gap-1 flex-wrap">
              {/* Add Scanner */}
              <div className="relative" ref={screenerTechRef}>
                <button onClick={() => { setScreenerTechOpen(o => !o); setScreenerPatternOpen(false); }}
                  className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[12px] font-medium"
                  style={{ backgroundColor: screenerTechFilters.size > 0 ? `${colors.accent}20` : colors.inputBg, color: screenerTechFilters.size > 0 ? colors.accent : colors.secondaryText, borderWidth: '1px', borderColor: screenerTechFilters.size > 0 ? colors.accent : colors.border }}>
                  + Scanner
                  {screenerTechFilters.size > 0 && <span className="ml-0.5 px-1 rounded-full text-[10px] font-bold" style={{ backgroundColor: colors.accent, color: '#fff' }}>{screenerTechFilters.size}</span>}
                  <ChevronRight className="w-3 h-3" style={{ transform: screenerTechOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }} />
                </button>
                {screenerTechOpen && (
                  <div className="app-card absolute left-0 top-full mt-1 z-50 w-72 rounded-lg shadow-xl overflow-y-auto"
                    style={{ backgroundColor: colors.cardBg, borderWidth: '1px', borderColor: colors.border, maxHeight: '280px' }}>
                    {Object.entries(SCANNER_CATEGORY_LABELS).map(([catKey, catMeta]) => {
                      const scannersInCat = Object.entries(SCANNER_INFO).filter(([n]) => SCANNER_CATEGORY_MAP[n] === catKey);
                      return (
                        <div key={catKey}>
                          <p className="app-card px-1.5 py-0.5 text-[12px] font-bold uppercase tracking-wide sticky top-0"
                            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: colors.accent }}>{catMeta.label}</p>
                          {scannersInCat.map(([name, info]) => {
                            const selected = screenerTechFilters.has(name);
                            const dirColor = info.direction === 'bullish' ? colors.profit : info.direction === 'bearish' ? colors.loss : colors.accent;
                            return (
                              <button key={name} onClick={() => { const n = new Set(screenerTechFilters); if (selected) n.delete(name); else n.add(name); setScreenerTechFilters(n); }}
                                className="w-full flex items-center justify-between px-1.5 py-0.5 text-left text-[12px] hover:opacity-80"
                                style={{ backgroundColor: selected ? `${dirColor}15` : 'transparent', color: colors.primaryText }}>
                                <span>{info.label}</span>
                                {selected && <span style={{ color: dirColor }}>?</span>}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Add Pattern */}
              <div className="relative" ref={screenerPatternRef}>
                <button onClick={() => { setScreenerPatternOpen(o => !o); setScreenerTechOpen(false); }}
                  className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[12px] font-medium"
                  style={{ backgroundColor: screenerPatternFilters.size > 0 ? `${colors.accent}20` : colors.inputBg, color: screenerPatternFilters.size > 0 ? colors.accent : colors.secondaryText, borderWidth: '1px', borderColor: screenerPatternFilters.size > 0 ? colors.accent : colors.border }}>
                  + Pattern
                  {screenerPatternFilters.size > 0 && <span className="ml-0.5 px-1 rounded-full text-[10px] font-bold" style={{ backgroundColor: colors.accent, color: '#fff' }}>{screenerPatternFilters.size}</span>}
                  <ChevronRight className="w-3 h-3" style={{ transform: screenerPatternOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }} />
                </button>
                {screenerPatternOpen && (
                  <div className="app-card absolute left-0 top-full mt-1 z-50 w-56 rounded-lg shadow-xl overflow-y-auto"
                    style={{ backgroundColor: colors.cardBg, borderWidth: '1px', borderColor: colors.border, maxHeight: '240px' }}>
                    {Object.entries(PATTERN_INFO).map(([ptype, info]) => {
                      const selected = screenerPatternFilters.has(ptype);
                      return (
                        <button key={ptype} onClick={() => { const n = new Set(screenerPatternFilters); if (selected) n.delete(ptype); else n.add(ptype); setScreenerPatternFilters(n); }}
                          className="w-full flex items-center justify-between px-1.5 py-0.5 text-left text-[12px] hover:opacity-80"
                          style={{ backgroundColor: selected ? `${info.color}15` : 'transparent', color: colors.primaryText }}>
                          <span>{info.label}</span>
                          <span className="text-[12px] ml-2" style={{ color: info.color }}>{info.description}</span>
                          {selected && <span className="ml-1" style={{ color: info.color }}>?</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="w-px h-3.5" style={{ backgroundColor: colors.border }} />
              {/* Timeframe */}
              {['swing', '5min', '15min'].map(tf => (
                <button key={tf} onClick={() => { setScreenerTimeframe(tf); setScreenerTechFilters(new Set()); setScreenerPatternFilters(new Set()); }}
                  className="px-1 py-0.5 rounded text-[12px] font-semibold"
                  style={{ backgroundColor: screenerTimeframe === tf ? colors.accent : colors.inputBg, color: screenerTimeframe === tf ? '#fff' : colors.secondaryText, borderWidth: '1px', borderColor: screenerTimeframe === tf ? colors.accent : colors.border }}>
                  {tf === 'swing' ? 'Swing' : tf}
                </button>
              ))}
              {/* Clear all */}
              {activeFilterCount > 0 && (
                <button onClick={() => { setScreenerSector(null); setScreenerPriceRange(null); setScreenerTechFilters(new Set()); setScreenerPatternFilters(new Set()); setScreenerSearch(''); }}
                  className="px-1 py-0.5 rounded text-[12px]"
                  style={{ backgroundColor: colors.inputBg, color: colors.secondaryText, borderWidth: '1px', borderColor: colors.border }}>
                  ? Clear
                </button>
              )}
            </div>

            {/* Active filter chips */}
            {(screenerTechFilters.size > 0 || screenerPatternFilters.size > 0) && (
              <div className="flex flex-wrap gap-1">
                {[...screenerTechFilters].map(name => {
                  const info = SCANNER_INFO[name];
                  const dirColor = info?.direction === 'bullish' ? colors.profit : info?.direction === 'bearish' ? colors.loss : colors.accent;
                  return (
                    <span key={name} className="flex items-center px-1.5 py-0.5 rounded-full text-[12px] font-medium"
                      style={{ backgroundColor: `${dirColor}20`, color: dirColor, borderWidth: '1px', borderColor: `${dirColor}40` }}>
                      {info?.label || name}
                      <button onClick={() => { const n = new Set(screenerTechFilters); n.delete(name); setScreenerTechFilters(n); }} className="ml-1">?</button>
                    </span>
                  );
                })}
                {[...screenerPatternFilters].map(ptype => {
                  const info = PATTERN_INFO[ptype];
                  return (
                    <span key={ptype} className="flex items-center px-1.5 py-0.5 rounded-full text-[12px] font-medium"
                      style={{ backgroundColor: `${info?.color || colors.accent}20`, color: info?.color || colors.accent, borderWidth: '1px', borderColor: `${info?.color || colors.accent}40` }}>
                      {info?.label || ptype}
                      <button onClick={() => { const n = new Set(screenerPatternFilters); n.delete(ptype); setScreenerPatternFilters(n); }} className="ml-1">?</button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Results table */}
            <div className="app-card rounded-lg overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
              {screenerLoading ? (
                <div className="flex items-center justify-center py-3">
                  <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: colors.accent, borderTopColor: 'transparent' }} />
                </div>
              ) : results.length === 0 ? (
                <div className="text-center py-3">
                  <Activity className="w-8 h-8 mx-auto mb-2" style={{ color: colors.secondaryText }} />
                  <p className="text-[12px]" style={{ color: colors.secondaryText }}>No stocks match your filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                  <div style={{ minWidth: '480px' }}>
                  <div className="grid px-3 py-2 text-[12px] font-semibold uppercase tracking-wide"
                    style={{ gridTemplateColumns: '90px 1fr 110px 80px 60px', borderBottom: `1px solid ${colors.border}`, color: colors.secondaryText, gap: '8px' }}>
                    <span>Symbol</span><span>Company</span><span>Sector</span><span className="text-right">Price</span><span className="text-right">Chg%</span>
                  </div>
                  {results.map((s, idx) => {
                    const price  = s.current_price;
                    const chgPct = s.change_pct;
                    const chgUp  = chgPct >= 0;
                    const matched = [
                      ...[...screenerTechFilters].map(n => SCANNER_INFO[n]?.label || n),
                      ...[...screenerPatternFilters].map(p => PATTERN_INFO[p]?.label || p),
                    ];
                    return (
                      <div key={idx}
                        onClick={() => handleStockClick(s.symbol)}
                        className="grid px-3 py-2 cursor-pointer transition-colors"
                        style={{ gridTemplateColumns: '90px 1fr 110px 80px 60px', gap: '8px', borderBottom: idx < results.length - 1 ? `1px solid ${colors.border}` : 'none', backgroundColor: 'transparent' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = `${colors.accent}08`}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <span className="font-bold text-[12px] self-center" style={{ color: colors.primaryText }}>{s.symbol.replace('.NS', '')}</span>
                        <div className="min-w-0 self-center">
                          <p className="text-[12px] truncate" style={{ color: colors.primaryText }}>{s.company_name}</p>
                          {matched.length > 0 && (
                            <p className="text-[12px] truncate mt-0.5" style={{ color: colors.accent }}>{matched.join(' — ')}</p>
                          )}
                        </div>
                        <span className="text-[12px] self-center truncate" style={{ color: colors.secondaryText }}>{s.sector || '…'}</span>
                        <span className="text-[12px] text-right self-center font-medium" style={{ color: colors.primaryText }}>
                          {price ? formatPrice(price, 0) : '…'}
                        </span>
                        <span className="text-[12px] text-right self-center font-semibold"
                          style={{ color: chgPct != null ? (chgUp ? colors.profit : colors.loss) : colors.secondaryText }}>
                          {chgPct != null ? `${chgUp ? '+' : ''}${chgPct.toFixed(1)}%` : '…'}
                        </span>
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()
}
    </div>

    {/* Chart Modal — opens when a pattern card is clicked */}
    {chartPattern && (() => {
      const info = PATTERN_INFO[chartPattern.pattern_type] || { label: chartPattern.pattern_type, color: colors.accent };
      const sym = chartPattern.symbol?.replace('.NS', '');
      const defaultTf = patternMode === 'intraday' ? (intradayTf === '15min' ? '15m' : '5m') : '1d';
      return (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: colors.background || '#0f1117' }}>
          {/* Modal header */}
          <div className="app-card flex items-center justify-between px-1.5 py-0.5 flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderBottom: `1px solid ${colors.border}` }}>
            <div className="flex items-center space-x-1.5 min-w-0">
              <span className="font-bold text-[12px] truncate" style={{ color: colors.primaryText }}>{sym}</span>
              <span className="px-1.5 py-0.5 rounded text-[12px] font-medium flex-shrink-0" style={{ backgroundColor: `${info.color}20`, color: info.color }}>
                {info.label}
              </span>
              {chartPattern.confidence && (
                <span className={`text-[12px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 hidden sm:inline ${chartPattern.confidence === 'high' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}`}>
                  {chartPattern.confidence}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-1.5 flex-shrink-0">
              <button
                onClick={() => { handleCloseChartModal(); navigate(`/stock/${sym}`); }}
                className="flex items-center space-x-1 px-1 py-0.5 rounded-lg text-[12px] font-medium"
                style={{ backgroundColor: `${colors.accent}20`, color: colors.accent, borderWidth: '1px', borderColor: colors.accent }}
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">View Stock Page</span>
              </button>
              <button onClick={handleCloseChartModal} className="p-1.5 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
                <X className="w-4 h-4" style={{ color: colors.primaryText }} />
              </button>
            </div>
          </div>
          {/* Chart */}
          <div className="flex-1 overflow-hidden">
            <TradingChart
              symbol={sym}
              patterns={[chartPattern]}
              defaultTimeframe={defaultTf}
              stretch
              showBuySell={true}
              onBuy={() => openTradePanel(chartPattern, 'BUY')}
              onSell={() => openTradePanel(chartPattern, 'SELL')}
            />
          </div>
        </div>
      );
    })()}

    {/* Scanner Chart Modal */}
    {chartSignal && (() => {
      const info = SCANNER_INFO[chartSignal.scanner_name] || { label: chartSignal.scanner_name, color: colors.accent, direction: 'neutral' };
      const sym = chartSignal.symbol?.replace('.NS', '');
      const autoIndicators = SCANNER_TO_INDICATOR[chartSignal.scanner_name] || [];
      const isBullish = info.direction === 'bullish';
      const isBearish = info.direction === 'bearish';
      return (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: colors.background || '#0f1117' }}>
          <div className="app-card flex items-center justify-between px-1.5 py-0.5 flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderBottom: `1px solid ${colors.border}` }}>
            <div className="flex items-center space-x-1.5 min-w-0">
              <span className="font-bold text-[12px] truncate" style={{ color: colors.primaryText }}>{sym}</span>
              <span className="px-1.5 py-0.5 rounded text-[12px] font-medium flex-shrink-0" style={{ backgroundColor: `${info.color}20`, color: info.color }}>
                {info.label}
              </span>
              {(() => {
                const sigCfg = SIGNAL_STATUS[getSignalStatus(chartSignal.triggered_at)];
                return (
                  <span className="text-[12px] px-1.5 py-0.5 rounded font-bold flex-shrink-0 hidden sm:inline" style={{ backgroundColor: sigCfg.bg, color: sigCfg.color }}>
                    {sigCfg.label}
                  </span>
                );
              })()}
            </div>
            <div className="flex items-center space-x-1.5 flex-shrink-0">
              <button
                onClick={() => setScannerSocialOpen(true)}
                className="flex items-center space-x-1 px-1.5 py-0.5 rounded-lg text-[12px] font-bold"
                style={{ background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }}
              >
                <Share2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Post</span>
              </button>
              <button
                onClick={() => { setChartSignal(null); navigate(`/stock/${sym}`); }}
                className="flex items-center space-x-1 px-1 py-0.5 rounded-lg text-[12px] font-medium"
                style={{ backgroundColor: `${colors.accent}20`, color: colors.accent, borderWidth: '1px', borderColor: colors.accent }}
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">View Stock Page</span>
              </button>
              <button onClick={() => setChartSignal(null)} className="p-1.5 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
                <X className="w-4 h-4" style={{ color: colors.primaryText }} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <TradingChart
              symbol={sym}
              defaultTimeframe="1d"
              defaultIndicators={autoIndicators}
              stretch
              showBuySell={true}
              onBuy={!isBearish ? () => { setChartSignal(null); openScannerTradePanel(chartSignal, 'BUY'); } : null}
              onSell={!isBullish ? () => { setChartSignal(null); openScannerTradePanel(chartSignal, 'SELL'); } : null}
            />
          </div>
        </div>
      );
    })()}

    {/* Hidden scanner share card for html2canvas */}
    {chartSignal && (() => {
      const info = SCANNER_INFO[chartSignal.scanner_name] || { label: chartSignal.scanner_name, color: colors.accent, direction: 'neutral', description: '' };
      return (
        <div style={{ position: 'fixed', left: -9999, top: 0, opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
          <ScannerShareCard cardRef={scannerCardRef} snap={{
            symbol: chartSignal.symbol || '',
            scanner_name: chartSignal.scanner_name,
            scanner_label: info.label,
            scanner_description: info.description || '',
            direction: info.direction || 'neutral',
            color: info.color || colors.accent,
            triggered_at: chartSignal.triggered_at,
            strengthPct: chartSignal.strengthPct ?? null,
          }} />
        </div>
      );
    })()}

    {/* Scanner social share bottom sheet */}
    {scannerSocialOpen && chartSignal && (() => {
      const info = SCANNER_INFO[chartSignal.scanner_name] || { label: chartSignal.scanner_name, color: colors.accent, direction: 'neutral', description: '' };
      const snapData = {
        symbol: chartSignal.symbol || '',
        scanner_name: chartSignal.scanner_name,
        scanner_label: info.label,
        scanner_description: info.description || '',
        direction: info.direction || 'neutral',
        color: info.color || colors.accent,
        triggered_at: chartSignal.triggered_at,
        strengthPct: chartSignal.strengthPct ?? null,
      };
      return (
        <div className="fixed inset-0 z-[60] flex items-end justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setScannerSocialOpen(false)}>
          <div className="w-full max-w-sm rounded-t-2xl p-5 space-y-4" style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[15px] font-bold" style={{ color: colors.primaryText }}>Share Scanner Signal</p>
              <button onClick={() => setScannerSocialOpen(false)}><X className="w-4 h-4" style={{ color: colors.secondaryText }} /></button>
            </div>
            <div style={{ width: '100%', height: Math.round(1350 * (300/1080)), overflow: 'hidden', borderRadius: 10 }}>
              <div style={{ width: 1080, height: 1350, transform: `scale(${300/1080})`, transformOrigin: 'top left' }}>
                <ScannerShareCard snap={snapData} />
              </div>
            </div>
            <p className="text-[12px] text-center" style={{ color: colors.secondaryText }}>Opens share sheet — pick Instagram, WhatsApp, or save to camera roll.</p>
            <button onClick={handleScannerShare} disabled={scannerCapturing} className="w-full py-3 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2" style={{ background: colors.accent, color: '#fff', opacity: scannerCapturing ? 0.6 : 1 }}>
              {scannerCapturing ? <><Loader className="w-4 h-4 animate-spin" /> Preparing…</> : <><Share2 className="w-4 h-4" /> Share as Image</>}
            </button>
          </div>
        </div>
      );
    })()}

    {/* TradeOptionsSheet — dual option comparison when pattern + scanner both exist */}
    {tradeOptionsOpen && tradeResolution && tradeOptionsSignal && (
      <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center" style={{ backgroundColor: colors.overlayBg }}>
        <div className="app-card w-full max-w-xl rounded-t-2xl md:rounded-2xl p-1 space-y-4 max-h-[90vh] overflow-y-auto"
          style={{ background: `linear-gradient(135deg, ${colors.accent}40 0%, ${colors.accent}15 100%), ${colors.cardBg}`, border: `1px solid ${colors.accentBorder}` }}>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-[17px]" style={{ color: colors.primaryText }}>
                Two trade setups found — {tradeOptionsSignal.symbol?.replace('.NS', '')}
              </h3>
              <p className="text-[12px] mt-0.5" style={{ color: colors.secondaryText }}>
                Both a chart pattern and scanner signal exist. Pick the best setup.
              </p>
            </div>
            <button onClick={() => setTradeOptionsOpen(false)} className="p-1.5 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
              <X className="w-4 h-4" style={{ color: colors.primaryText }} />
            </button>
          </div>

          {/* Recommendation banner */}
          <div className="rounded-lg p-1.5" style={{ backgroundColor: `${colors.accent}15`, borderWidth: '1px', borderColor: `${colors.accent}40` }}>
            <p className="text-[12px] font-semibold mb-0.5" style={{ color: colors.accent }}>
              Recommended: Option {tradeResolution.recommended}
            </p>
            <p className="text-[12px]" style={{ color: colors.secondaryText }}>{tradeResolution.reason}</p>
          </div>

          {/* Options side by side */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'A', opt: tradeResolution.optionA },
              { key: 'B', opt: tradeResolution.optionB },
            ].map(({ key, opt }) => {
              if (!opt) return null;
              const isRec = tradeResolution.recommended === key;
              return (
                <div key={key} className="rounded-lg p-1.5 space-y-2"
                  style={{
                    backgroundColor: colors.inputBg,
                    borderWidth: '2px',
                    borderColor: isRec ? colors.accent : colors.border,
                  }}>
                  <div className="flex items-center justify-between">
                    <span className="text-[12px] font-semibold" style={{ color: colors.primaryText }}>Option {key}</span>
                    {isRec && (
                      <span className="text-[12px] px-1.5 py-0.5 rounded font-bold"
                        style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>
                        Best
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] font-medium" style={{ color: colors.secondaryText }}>{opt.label}</p>
                  {!opt.isValid && (
                    <p className="text-[12px]" style={{ color: '#eab308' }}>SL not at structural level</p>
                  )}
                  <div className="space-y-1 text-[12px]">
                    {[
                      ['Entry', formatPrice(opt.entry)],
                      ['SL', formatPrice(opt.sl)],
                      ['Target', formatPrice(opt.target)],
                      ['R:R', `1:${opt.rrRatio}`],
                      ['Qty', opt.qty],
                      ['Risk', formatPrice(opt.actualRisk)],
                    ].map(([label, val]) => (
                      <div key={label} className="flex justify-between">
                        <span style={{ color: colors.secondaryText }}>{label}</span>
                        <span style={{ color: colors.primaryText }}>{val}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => openTradeFromResolution(opt)}
                    className="w-full py-0.5 rounded-lg text-[12px] font-semibold mt-1"
                    style={{
                      backgroundColor: isRec ? colors.accent : colors.inputBg,
                      color: isRec ? '#fff' : colors.primaryText,
                      borderWidth: '1px',
                      borderColor: isRec ? colors.accent : colors.border,
                    }}
                  >
                    Trade with {key}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    )}

    {/* Reuse existing OrderModal with pattern pre-fill */}
    <OrderModal
      isOpen={orderModalOpen}
      onClose={() => setOrderModalOpen(false)}
      stock={orderModalStock}
      orderType={orderModalSide}
      initialValues={orderModalPrefill}
      onOrderUpdated={() => setOrderModalOpen(false)}
    />
    </>
  );
}

export default ScannersPage;
